import { Dispatch, SetStateAction, useState, useEffect } from 'react'
import { BaseProvider } from '@metamask/providers'
import { ethers } from 'ethers'
import axios from 'axios'

import { useRecoilState } from 'recoil'
import { pageState } from '../../../../../src/atoms/PageAtom'
import { pastPageState } from '../../../../../src/atoms/PastPageAtom'
import { useTranslation } from 'react-i18next'

import IncreaseIcon from '../../../../../src/assets/images/icons/circle-increase.svg'
import DecreaseIcon from '../../../../../src/assets/images/icons/circle-decrease.svg'

type Window = {
  ethereum: BaseProvider
  location: Location
}
declare let window: Window

type ContractABI = Array<ContractInterface>

interface ContractInterface {
  constant?: boolean
  inputs?: Array<ABIInput>
  name?: string
  outputs?: Array<ABIOutput>
  payable?: boolean
  stateMutability?: 'pure' | 'view' | 'nonpayable' | 'payable'
  type?: 'function' | 'constructor' | 'event' | 'fallback'
  anonymous?: boolean
}

interface ABIInput {
  name: string
  type: string
  indexed?: boolean
}

interface ABIOutput {
  name: string
  type: string
}

type Artist = {
  public_id: string
  display_name: string
}

type Generative = {
  public_id: string
  contract_address: string
  name: string
  chain_id: number
  uri: string
  artist: Artist
}

type PickupGenerative = {
  abi_uri: string
  mint_func: string
  price: string
  outer_link: string
  cover_image_uri: string
  music_uri: string
  max_supply: number
  total_supply: number
  generative: Generative
}

type Props = {
  web3: Web3
  pickupGenerative: PickupGenerative | undefined
  setOpenGenerative: Dispatch<SetStateAction<PickupGenerative | undefined>>
}

function GenerativeModal(props: Props) {
  const { t } = useTranslation()
  const [, setPage] = useRecoilState(pageState)
  const [, setPastPage] = useRecoilState(pastPageState)

  const [unit, setUnit] = useState<string>('ETH')
  const [quantity, setQuantity] = useState<number>(1)

  const [contract, setContract] = useState<ethers.Contract>()
  const [isTransaction, setIsTransaction] = useState<boolean>(false)

  useEffect(() => {
    if (!props.pickupGenerative) return
    tokenNetworkUnit()
  }, [props.pickupGenerative])

  useEffect(() => {
    return () => {
      setQuantity(1)
      props.setOpenGenerative(undefined)
    }
  }, [])

  useEffect(() => {
    if (!props.web3.signer && props.web3.provider) return
    contractInit()
  }, [props.web3, props.web3.provider])

  const tokenNetworkUnit = () => {
    switch (props.pickupGenerative?.generative.chain_id) {
      case 1:
        setUnit('ETH')
        break
      case 5:
        setUnit('ETH(Goerli)')
        break
      case 137:
        setUnit('MATIC')
        break
    }
  }

  // ネットワークの切り替え
  const changeNetwork = async () => {
    if (!props.web3.signer) return

    let chainId = '0x1'
    switch (props.pickupGenerative?.generative.chain_id) {
      case 1:
        chainId = '0x1'
        break
      case 5:
        chainId = '0x5'
        break
      case 137:
        chainId = '0x89'
        break
    }
    return await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [
        {
          chainId: chainId,
        },
      ],
    })
  }

  // コントラクト関係処理
  const fetchAbi = async (): Promise<ContractABI> => {
    if (!props.pickupGenerative || !props.pickupGenerative.abi_uri)
      throw new Error('ABIが見つかりません')

    let abi: ContractABI = []
    await axios({
      method: 'GET',
      url: props.pickupGenerative.abi_uri,
    })
      .then((response) => {
        abi = response.data
      })
      .catch((error) => {
        console.error('error', error)
      })
    return abi
  }

  const contractInit = async () => {
    if (!props.web3.signer || !props.pickupGenerative?.generative.chain_id)
      return

    const network = await props.web3.provider.getNetwork()
    if (props.pickupGenerative.generative.chain_id !== network.chainId) {
      await changeNetwork()
    }
    const abi = await fetchAbi()

    const generativeContract = new ethers.Contract(
      props.pickupGenerative.generative.contract_address,
      abi,
      props.web3.signer
    )

    setContract(generativeContract)
  }

  const mint = async (): Promise<void> => {
    if (!props.web3.signer || !props.pickupGenerative || !contract) return

    const network = await props.web3.provider.getNetwork()

    if (props.pickupGenerative.generative.chain_id !== network.chainId) {
      changeNetwork()
      return alert('ネットワークが異なります')
    }

    setIsTransaction(true)

    const unitPrice = Number(props.pickupGenerative.price)
    const adjNum = 1000000000
    const quantityPrice = (unitPrice * adjNum * quantity) / adjNum
    const totalEtherValue = ethers.utils.parseEther(quantityPrice.toString())

    const tx = await contract[props.pickupGenerative.mint_func](quantity, {
      value: totalEtherValue,
    }).catch((error: Error) => {
      const message = error.message
      if (message.indexOf('caller is not token owner or approved') != -1) {
        alert('出品者が転送を許可していなため購入できませんでした。')
      } else if (message.indexOf('user rejected transaction') != -1) {
        alert('キャンセルしました')
      } else if (message.indexOf('insufficient balance for transfer') != -1) {
        alert('購入に失敗しました')
      } else if (message.indexOf('maxSupply over.') != -1) {
        alert('最大供給量をオーバーしています')
      } else if (message.indexOf('mintLimit over.') != -1) {
        alert('Mint可能な上限数をオーバーしています')
      } else if (message.indexOf('outside the period') != -1) {
        alert('アーリーアクセス期間中です')
      } else if (message.indexOf('execution reverted') != -1) {
        alert('トランザクションエラー')
      } else if (
        message.indexOf('Value sent does not meet price for NFT') != -1
      ) {
        alert('コントラクトに送信した資金額が正しくありません')
      } else if (
        message.indexOf('insufficient funds for gas * price + value') != -1
      ) {
        alert(
          'ウォレット内の資金が不足しているため購入できません。\n（購入にはNFT自体の価格に加え、ガス代も必要となります。）'
        )
      }
      setIsTransaction(false)
    })

    if (!tx) return

    await tx
      .wait()
      .then(() => {
        alert(
          '購入が完了しました！\nマイミュージックを更新してお聴きいただけます。'
        )
        setPastPage('home')
        setPage('mymusic')
      })
      .catch((error: Error) => {
        alert('購入に失敗しました')
        console.error(error.message)
      })
    setIsTransaction(false)
  }

  const isContentScroll = (): boolean => {
    const element = document.querySelector('.content')
    if (!element) return false

    if (element.scrollHeight > element.clientHeight) return true

    return false
  }

  return (
    <div className={`generative-modal ${props.pickupGenerative && 'open'}`}>
      <div
        className="overlay"
        onClick={() => {
          if (isTransaction)
            return alert('トランザクション完了までお待ちください')
          props.setOpenGenerative(undefined)
        }}
      ></div>

      <div className="modal-body">
        <div
          className="image"
          style={{
            backgroundImage: `url(${props.pickupGenerative?.cover_image_uri})`,
          }}
        ></div>
        <div
          className={`content ${
            props.pickupGenerative?.mint_func || isContentScroll()
              ? ''
              : 'content-center'
          }`}
        >
          <h2 className="data ttl">
            {props.pickupGenerative?.generative.name}
          </h2>
          <button
            className="data button"
            onClick={(event) => {
              event.stopPropagation()
              setPastPage('home')
              setPage(
                `artists/${props.pickupGenerative?.generative.artist.public_id}`
              )
            }}
          >
            <span className="user-icon"></span>
            <span className="artist-name">
              {props.pickupGenerative?.generative.artist.display_name}
            </span>
          </button>
          <div className="supply-area">
            <p className="supply-title">発行枚数</p>
            <p className="supply-nums">
              {props.pickupGenerative?.total_supply.toLocaleString('en-US')}/
              {props.pickupGenerative?.max_supply.toLocaleString('en-US')}
            </p>
            {/* <div className="indicator">
              <div className="note-icon"></div>
              <div className="meter">
                <div
                  className="meter-main"
                  style={{
                    width: `${
                      props.pickupGenerative
                        ? (props.pickupGenerative.total_supply /
                            props.pickupGenerative.max_supply) *
                          100
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div> */}
            <div className="price-label">
              MINT PRICE&nbsp;&nbsp;{props.pickupGenerative?.price}&nbsp;{unit}
            </div>
            {props.pickupGenerative?.mint_func && (
              <div className="controller">
                <button
                  className="btn decrease"
                  style={{ backgroundImage: `url(${DecreaseIcon})` }}
                  onClick={() => {
                    if (quantity <= 1) return
                    setQuantity(quantity - 1)
                  }}
                ></button>
                <p className="quantity">{quantity}</p>
                <button
                  className="btn increase"
                  style={{ backgroundImage: `url(${IncreaseIcon})` }}
                  onClick={() => {
                    if (
                      props.pickupGenerative &&
                      quantity >= props.pickupGenerative?.max_supply
                    )
                      return
                    setQuantity(quantity + 1)
                  }}
                ></button>
              </div>
            )}
            <div className="modal-buttons">
              {props.pickupGenerative?.mint_func && (
                <button
                  onClick={() => {
                    if (isTransaction) return alert('現在Transaction中です')
                    if (props.web3.signer && contract) {
                      mint()
                    } else if (props.web3.signer && !contract) {
                      contractInit()
                    } else {
                      if (!window.ethereum)
                        return alert('MetaMaskをインストールしてください')
                      return alert(t('components.modal.notWalletConnected'))
                    }
                  }}
                  className="modal-button"
                >
                  {isTransaction ? 'Transaction中' : 'MINTする'}
                </button>
              )}

              {props.pickupGenerative?.outer_link && (
                <a
                  href={props.pickupGenerative?.outer_link}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="modal-button"
                >
                  <span className="icon outer-link"></span>
                  MintサイトでMINTする
                </a>
              )}
            </div>
            <button
              onClick={() => {
                setPastPage('home')
                setPage('articles/metamask_pc/detail')
              }}
              className="article-link"
            >
              MetaMaskについて
            </button>
            <button
              onClick={() => {
                setPastPage('home')
                setPage('others/terms_fan')
              }}
              className="article-link"
            >
              利用規約
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GenerativeModal
